import {
  SESSION_FETCH_OPTIONS,
  clearLegacySessionToken,
  probeSession,
} from '@/lib/session';
import { apiUrl, BACKEND_URL } from '@/lib/apiUrl';

const PUBLIC_PATHS = ["/", "/login", "/auth/callback", "/auth-test"];
let unauthorizedSessionCheck = null;

function redirectToLoginIfPrivate() {
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    window.location.assign("/login");
  }
}

/**
 * Treat /api/auth/me as the sole authority for whether the app session expired.
 * A feature endpoint can return 401 for its own authorization rules while the
 * user's cookie is still valid; redirecting immediately creates a login loop.
 */
export function confirmSessionBeforeRedirect({
  verify = probeSession,
  clear = clearLegacySessionToken,
  redirect = redirectToLoginIfPrivate,
} = {}) {
  if (unauthorizedSessionCheck) return unauthorizedSessionCheck;

  unauthorizedSessionCheck = (async () => {
    const sessionState = await verify();
    if (sessionState === "unauthenticated") {
      clear();
      redirect();
    }
    return sessionState;
  })().finally(() => {
    unauthorizedSessionCheck = null;
  });

  return unauthorizedSessionCheck;
}

export function resetUnauthorizedSessionGuardForTests() {
  unauthorizedSessionCheck = null;
}

export function authFetch(url, options = {}) {
  if (typeof url === 'string' && url.includes('undefined')) {
    throw new Error(`Invalid API URL: ${url}`);
  }
  return fetch(url, {
    ...SESSION_FETCH_OPTIONS,
    ...options,
    credentials: 'include',
    headers: { ...(options.headers || {}) },
  }).then((res) => {
    const path = typeof url === "string" ? url : "";
    const isSessionProbe = path.includes("/api/auth/me") || path.includes("/api/auth/session");
    const isPublicPath = PUBLIC_PATHS.includes(window.location.pathname);
    if (res.status === 401 && !isSessionProbe && !isPublicPath) {
      void confirmSessionBeforeRedirect();
    }
    return res;
  });
}

export function logout() {
  clearLegacySessionToken();
  return fetch(`/api/auth/logout`, {
    method: 'POST',
    ...SESSION_FETCH_OPTIONS,
  });
}

export { BACKEND_URL, apiUrl };
